/**
 * O que cada módulo do sistema entrega ao usuário, reunido em um só lugar.
 *
 * O modal do ícone de olho (ModuloResumoComponent) busca a lista por aqui a
 * partir da rota do módulo, então os hubs não precisam mais carregar esse texto
 * nos seus próprios models. A chave é a rota, que é o dado estável entre o
 * cadastro do módulo, o menu e o front.
 */
export const RECURSOS_POR_ROTA: Record<string, string[]> = {
  // --- Módulos do sistema (home do administrativo) ---
  '/adm/pncp/permissoesPncp': [
    'Permissões de integração com o Portal Nacional de Contratações Públicas',
    'Entidades habilitadas para envio das contratações',
    'Consulta da situação de cada integração',
    'Configuração das credenciais de acesso ao PNCP'
  ],
  '/adm/certificados': [
    'Certificados digitais vinculados aos sites ativos',
    'Consulta por entidade, validade e situação',
    'Acompanhamento dos certificados próximos do vencimento',
    'Upload e substituição do certificado da entidade'
  ],
  '/adm/migracao-dados': [
    'Lotes de migração enviados para o sistema',
    'Situação de cada carga: concluída, em processamento ou com erro',
    'Consulta dos registros migrados por entidade e período',
    'Reprocessamento das cargas com inconsistência'
  ],
  '/adm/imap/cliente': [
    'Entidades administrativas atendidas pela plataforma',
    'Dados cadastrais, contato e situação de cada cliente',
    'Cadastro e edição da entidade',
    'Vínculo da entidade com os módulos contratados'
  ],
  '/adm/imap/modulo': [
    'Módulos disponibilizados no sistema, em estrutura de pai e filho',
    'Tipo, rota, ordem de exibição e situação de cada módulo',
    'Cadastro e edição de módulo',
    'Ativação e inativação do módulo para as entidades'
  ],
  '/adm/imap/usuario': [
    'Cadastro dos usuários do sistema e seus vínculos',
    'Perfis de acesso e permissões por módulo',
    'Grupos de entidades atendidas',
    'Logs de acesso e trilha de auditoria'
  ],

  // --- Cadastro de usuários ---
  '/adm/imap/usuario/cadastro': [
    'Lista de usuários com perfil, entidade vinculada e situação',
    'Cadastro e edição dos dados de acesso do usuário',
    'Vínculo do usuário com entidades e grupos',
    'Ativação e inativação do acesso ao sistema'
  ],
  '/adm/imap/usuario/perfis': [
    'Perfis de acesso cadastrados, com a quantidade de usuários em cada um',
    'Permissões por módulo e por tela do sistema',
    'Criação e edição de perfil',
    'Ativação e inativação do perfil'
  ],
  '/adm/imap/usuario/logs': [
    'Indicadores de acessos, sessões e tentativas do período',
    'Filtro por usuário, período e tipo de atividade',
    'Trilha de auditoria com data, hora, IP e ação realizada',
    'Exportação dos registros consultados'
  ],
  '/adm/imap/usuario/grupos': [
    'Grupos cadastrados e as entidades que compõem cada um',
    'Filtro por nome do grupo e por situação',
    'Criação e edição de grupo de entidades',
    'Inclusão e remoção de entidades do grupo'
  ],

  // --- Carta de serviços ---
  '/adm/carta-servicos/imap/modulo-carta-servicos': [
    'Módulos do portal público da carta de serviços',
    'Editor da home page, serviços, categorias e canais de atendimento',
    'e-SIC e Ouvidoria, com manifestações e prazos',
    'Notícias, links dinâmicos, perguntas frequentes e LGPD'
  ],
  '/adm/institucional': [
    'Lista única com todos os módulos do portal do cliente',
    'Módulos exclusivos da Prefeitura, da Câmara ou do Consórcio, conforme o tipo de portal',
    'Quantidade de itens e de rascunhos de cada módulo',
    'Busca de módulos por nome ou descrição'
  ],
  '/adm/carta-servicos/servicos': [
    'Categorias de serviço publicadas, com busca por nome',
    'Serviços de cada categoria com responsável, forma de atendimento e status',
    'Cadastro do serviço em etapas: dados, requisitos, documentos e responsáveis',
    'Publicação e despublicação do serviço no portal do cidadão',
    'Marcação de serviços em destaque'
  ],
  '/adm/carta-servicos/categorias-servicos': [
    'Categorias que organizam os serviços no portal',
    'Tipo, responsável e situação de cada categoria',
    'Cadastro e edição de categoria em modal, sem sair da lista',
    'Ativação e inativação da categoria'
  ],
  '/adm/carta-servicos/canais-atendimento': [
    'Indicadores de canais totais, ativos, inativos e pendentes',
    'Canais cadastrados com tipo, contato e situação',
    'Filtro por tipo de canal e por status',
    'Cadastro do canal com blocos de conteúdo próprios',
    'Escolha do comportamento de cada bloco: modal, página interna, nova aba ou accordion'
  ],
  '/adm/carta-servicos/metricas-atendimento': [
    'Visão geral com atendimentos por canal, status e tempo médio de resposta',
    'Evolução mensal e manifestações por tipo em gráficos',
    'Pedidos do e-SIC em acompanhamento, com prazo e SLA',
    'Aba de relatórios com filtros por canal, período, departamento, responsável e SLA',
    'Sete relatórios prontos: geral, SLA, departamento, responsável, ouvidoria, e-SIC e cartas',
    'Exportação dos dados em planilha, PDF ou impressão'
  ],
  '/adm/carta-servicos/noticias': [
    'Notícias publicadas, em revisão e em rascunho, com busca e filtro',
    'Cadastro em etapas com editor de texto, imagem de capa e seções',
    'Agendamento da publicação e controle de status',
    'Vinculação da notícia às redes sociais do portal'
  ],
  '/adm/carta-servicos/link-dinamico': [
    'Indicadores de links totais, ativos, inativos, externos e em modal',
    'Links cadastrados com local de exibição, destino e última atualização',
    'Escolha entre abrir o link em nova aba ou em modal interno',
    'Ativação e inativação do link sem removê-lo do cadastro'
  ],
  '/adm/carta-servicos/perguntas-frequentes': [
    'Perguntas publicadas, em rascunho e inativas, com filtro por categoria e status',
    'Indicadores do total de perguntas por situação',
    'Cadastro da pergunta com identificação e resposta na mesma tela',
    'Resposta escrita em accordions, ativados ou desativados um a um',
    'Reordenação dos accordions por arrasto e exclusão com confirmação'
  ],
  '/adm/carta-servicos/politica-atendimento': [
    'Perguntas que explicam a política de atendimento ao cidadão',
    'Filtro por categoria e por status da pergunta',
    'Cadastro da pergunta com identificação e resposta na mesma tela',
    'Resposta escrita em accordions, ativados ou desativados um a um',
    'Reordenação dos accordions por arrasto e exclusão com confirmação'
  ],
  '/adm/carta-servicos/lgpd': [
    'Dados do encarregado de dados (DPO) publicados ao cidadão',
    'Cargo, departamento, órgão, telefone, e-mail, horário e endereço de atendimento',
    'Seis perguntas frequentes fixas, com a resposta escrita pelo órgão',
    'Documentos e regulamentos anexados, com nome, categoria, tipo e data',
    'Cadastro de documento com upload em PDF, DOCX, XLSX ou ODT',
    'Edição e exclusão de documento, com controle de publicado ou rascunho'
  ],

  // --- Editor da home page ---
  '/adm/carta-servicos/editor-homepage': [
    'Editor dos blocos que formam a página inicial do portal',
    'Cabeçalho, banner, acesso rápido, redes sociais e rodapé',
    'Padrão de cores e ordem de exibição dos blocos na home',
    'Cada bloco é editado na sua própria tela, sem publicar o site inteiro'
  ],
  '/adm/carta-servicos/editor-homepage/cabecalho': [
    'Prévia do cabeçalho em tela larga e estreita, com o conteúdo publicado',
    'Brasão do ente enviado por upload, com prévia e remoção',
    'Nome do ente e nome curto exibidos ao lado do brasão',
    'Itens do menu, com ou sem dropdown de subitens, e a ordem de cada um',
    'Escolha entre manter o padrão do sistema ou publicar os links do órgão'
  ],
  '/adm/carta-servicos/editor-homepage/banner': [
    'Banners publicados, com nome do arquivo, tipo e data',
    'Envio da imagem com recorte na proporção do banner',
    'Cor predominante aplicada ao bloco do banner',
    'Importação e exportação dos dados do banner'
  ],
  '/adm/carta-servicos/editor-homepage/acesso-rapido': [
    'Prévia do bloco na página inicial, em tela larga e estreita',
    'Destaques com título, descrição, destino e link',
    'Ordem em que cada destaque aparece na home',
    'Ativação e inativação do destaque sem excluir o cadastro',
    'Cadastro e edição do destaque em modal, sem sair da lista'
  ],
  '/adm/carta-servicos/editor-homepage/rodape': [
    'Prévia do rodapé em tela larga e estreita, com o conteúdo publicado',
    'Exibição do brasão e do nome do ente ligada e desligada por bloco',
    'Links de acesso rápido, com ordem e limite de cinco ativos',
    'Endereço, CEP e horário de atendimento do órgão',
    'Telefones, e-mails e WhatsApp exibidos na coluna de contato',
    'Escolha de exibir ou não as redes sociais cadastradas'
  ],
  '/adm/carta-servicos/editor-homepage/redes-sociais': [
    'Redes cadastradas com link, ícone, cor do ícone e ordem',
    'Ativação e inativação de cada rede no portal',
    'Cadastro e edição da rede em modal',
    'Importação e exportação dos dados das redes'
  ],
  '/adm/carta-servicos/editor-homepage/padrao-cores-ordenacao': [
    'Paleta com os oito temas de cor disponíveis no portal',
    'Amostra do gradiente, da cor de destaque e da cor do texto de cada tema',
    'Ordem dos sete blocos da página inicial, por arrasto ou por setas',
    'Bloco ocultado sem perder o cadastro',
    'Prévia da página inicial sob demanda, no tema e na ordem escolhidos'
  ],

  // --- e-SIC ---
  '/adm/carta-servicos/sic': [
    'Painel do e-SIC com os três submódulos do serviço',
    'Gerenciar SIC: departamentos, áreas técnicas e responsáveis pelos pedidos',
    'Manifestações: pedidos recebidos, prazos, encaminhamentos e respostas',
    'Estatísticas: evolução dos pedidos, desempenho por departamento e prazos críticos'
  ],
  '/adm/carta-servicos/sic/gerenciar': [
    'Departamentos e áreas técnicas que respondem os pedidos',
    'Órgão superior, responsável técnico, e-mail e categorias de cada departamento',
    'Prazo interno de resposta definido por departamento',
    'Responsáveis pelo e-SIC, com perfil de acesso e departamentos atendidos',
    'Ativação e inativação de departamentos e responsáveis no fluxo do serviço'
  ],
  '/adm/carta-servicos/sic/manifestacoes': [
    'Indicadores de pedidos recebidos, em andamento, respondidos e atrasados',
    'Filtro por protocolo, assunto, status, período e departamento',
    'Lista com o prazo restante de cada pedido e destaque para os vencidos',
    'Detalhe do pedido com histórico, anexos e dados do solicitante',
    'Encaminhamento para departamento, prorrogação de prazo e pedido de complementação',
    'Conclusão do pedido com o registro da resposta ao cidadão'
  ],
  '/adm/carta-servicos/sic/estatisticas': [
    'Evolução mensal dos pedidos recebidos em gráfico',
    'Desempenho por departamento: recebidos, respondidos e atrasados',
    'Pedidos com prazo crítico, ordenados pelos dias restantes',
    'Tempo médio de resposta, taxa de recurso e índice de satisfação',
    'Comparação do tempo de resposta com o prazo padrão do e-SIC',
    'Exportação dos indicadores em CSV e PDF'
  ],

  // --- Ouvidoria ---
  '/adm/carta-servicos/ouvidoria': [
    'Painel da ouvidoria com os submódulos do serviço',
    'Criação de ouvidorias, departamentos e ouvidores responsáveis',
    'Manifestações recebidas, com encaminhamento e resposta ao cidadão',
    'Configuração das ouvidorias presenciais e digitais'
  ],
  '/adm/carta-servicos/ouvidoria/cadastro-ouvidorias': [
    'Lista das ouvidorias já criadas com município, órgão e responsável',
    'Indicadores de ouvidorias ativas, em implantação e inativas',
    'Distribuição das manifestações por departamento vinculado',
    'Importação e exportação da base de ouvidorias',
    'Cadastro em 4 blocos: dados básicos, contato, configurações e personalização',
    'Definição de prazo de resposta, SLA e tipos de manifestação aceitos'
  ],
  '/adm/carta-servicos/ouvidoria/departamentos': [
    'Lista dos departamentos com ícone, cor tema e responsável padrão',
    'Filtro por tipo de manifestação atendida pelo departamento',
    'Cadastro e edição do departamento em modal',
    'Ativação e desativação do departamento no fluxo de manifestações',
    'Ordem de exibição do departamento nas listas do sistema'
  ],
  '/adm/carta-servicos/ouvidoria/ouvidores': [
    'Lista de ouvidores com e-mail, telefone, órgão e ouvidoria vinculada',
    'Filtro por status e por ouvidoria vinculada',
    'Cadastro e edição dos dados pessoais e institucionais do ouvidor',
    'Definição do perfil de acesso e da data de nomeação',
    'Ativação e inativação do ouvidor no fluxo de atendimento'
  ],
  '/adm/carta-servicos/ouvidoria/manifestacoes': [
    'Consulta por situação, tipo, período e número de protocolo',
    'Painel com o total e o tempo médio de resposta por tipo de manifestação',
    'Lista dos pedidos em tramitação com prazo para resposta',
    'Resposta ao cidadão com anexos em PDF pela tela de detalhe',
    'Encaminhamento da manifestação para outra ouvidoria responsável',
    'Histórico completo das interações do protocolo'
  ]
};

/**
 * Rotas renomeadas na carta de serviços. A chave é o nome antigo, ainda presente
 * em cadastros do banco; o valor é a rota que o front reconhece hoje. A troca vale
 * também para as telas filhas, e a ordem importa: uma entrada pode reaproveitar o
 * resultado da anterior (/home/destaques → /editor-homepage/acesso-rapido).
 * Cada par tem o redirecionamento equivalente em carta-servicos-routing.module.ts.
 */
const ROTAS_RENOMEADAS: ReadonlyArray<readonly [string, string]> = [
  ['/adm/carta-servicos/home', '/adm/carta-servicos/editor-homepage'],
  ['/adm/carta-servicos/editor-homepage/destaques', '/adm/carta-servicos/editor-homepage/acesso-rapido'],
  ['/adm/carta-servicos/servicos', '/adm/carta-servicos/categorias-servicos'],
  ['/adm/carta-servicos/categoria-servicos', '/adm/carta-servicos/categorias-servicos'],
  ['/adm/carta-servicos/canais', '/adm/carta-servicos/canais-atendimento']
];

/** Tolera barra final, espaços, rota cadastrada sem a barra inicial e nome de rota antigo. */
export function normalizarRota(rota: string): string {
  const limpa = rota.split('?')[0].trim().replace(/\/+$/, '');
  let atual = limpa.startsWith('/') ? limpa : `/${limpa}`;

  for (const [antiga, nova] of ROTAS_RENOMEADAS) {
    if (atual === antiga || atual.startsWith(`${antiga}/`)) {
      atual = `${nova}${atual.slice(antiga.length)}`;
    }
  }

  return atual;
}

export function recursosDoModulo(rota?: string | null): string[] {
  if (!rota) {
    return [];
  }

  return RECURSOS_POR_ROTA[normalizarRota(rota)] ?? [];
}
